/**
 * Legenda das cores sobre o mapa (canto inferior esquerdo; no celular, recolhida
 * no canto inferior direito e aberta à direita do trilho de camadas): para cada camada
 * GOES visível, do topo para a base, a escala oficial da NOAA/STAR (ou a chave
 * do guia) e o que cada cor representa. Recolhível (a escolha fica guardada).
 *
 * Fica em posição absoluta sobre o mapa: não altera o tamanho do mapa nem da
 * barra de tempo. Sobe sozinha acima dos créditos quando eles chegam ao canto.
 */
import clsx from 'clsx';
import { ChevronDown, ChevronUp, Palette } from 'lucide-react';
import {
  Component,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ErrorInfo,
  type ReactNode,
  type RefObject,
} from 'react';
import { getProductName } from '../config/products';
import { PALETA_GSMAP } from '../gsmap/camada';
import { useGsmapStore } from '../gsmap/store';
import { useHoraNaTela } from '../gsmap/sync';
import { formatTimeBrt } from '../lib/time';
import { Escala } from '../previsao/LegendaFlutuante';
import { LegendaRadar } from '../radar/LegendaRadar';
import { DEF_PRODUTO, type Produto } from '../radar/produtos';
import { useRadarStore } from '../radar/store';
import { usePlanStore } from '../loop/plan';
import { useGoesStore } from '../state/goes';
import { shownTimeOf, useTimeStore } from '../state/time';
import { usePainelLateralAberto, useSemBaseNoQuadro } from '../ui/hooks';
import {
  daypartAt,
  getLegend,
  hasDayparts,
  isOfficialLegend,
  keyRows,
  legendImageUrl,
  legendLayers,
  pickGroup,
  type Daypart,
  type KeyGroup,
  type KeyLegend,
  type LegendImage,
  type LegendLayer,
  type LegendNote,
  type ProductLegend,
  type Swatch,
} from './catalog';
import { useLegendStore } from './store';
import { SIDEBAR_WIDTH } from '../map/viewControl';
import { FIM_DA_COLUNA_PX } from '../ui/layoutCelular';
import { useDeitado } from '../ui/useMediaQuery';

/** Distância das bordas do mapa (px). */
const EDGE_DESKTOP = 12;
const EDGE_MOBILE = 10;
/** Celular: a coluna de botões da direita fica a 12 px da borda; a legenda se alinha a ela. */
const EDGE_MOBILE_RIGHT = 12;
/**
 * No celular a legenda aberta fica acima do play (no meio do pé) e do botão da previsão, no canto
 * direito a 58 px do fundo (58 + 44 = 102 px; 08/10/2026). Recolhida, ela fica no canto inferior
 * DIREITO, abaixo da previsão (o esquerdo é da assinatura do pyForTraCC, e o trilho de camadas
 * desce até acima dela).
 */
const MOBILE_BOTTOM = 94;
/** Celular deitado: a previsão fica fora do caminho (na borda direita); basta passar do play (12 + 48 px). */
const MOBILE_BOTTOM_DEITADO = 54;
/** Espaço livre acima da legenda (botões do topo do mapa). */
const TOP_CLEARANCE = 64;
/**
 * Celular em pé: a legenda aberta vai da direita do trilho à borda direita e passa por baixo da
 * coluna de botões (voltar, "⋯", últimos focos, busca: até y 212), então o topo dela fica abaixo
 * do fim da coluna, com 8 px de folga.
 */
const TOP_CLEARANCE_MOBILE = FIM_DA_COLUNA_PX + 8;

// ---------------------------------------------------------------------------
// Peças visuais
// ---------------------------------------------------------------------------

/** CSS de uma amostra de cor (mesmo desenho da chave oficial). */
export function swatchBackground(swatch: Swatch): string {
  const [a, b = a] = swatch.colors;
  switch (swatch.pattern) {
    case 'split':
      return `linear-gradient(to right, ${a} 50%, ${b} 50%)`;
    case 'stacked':
      return `linear-gradient(to bottom, ${a} 50%, ${b} 50%)`;
    case 'diagonal':
      return `linear-gradient(135deg, ${a} 50%, ${b} 50%)`;
    case 'speckle':
      return `repeating-conic-gradient(${a} 0 25%, ${b} 0 50%) 0 0 / 4px 4px`;
    default:
      return a;
  }
}

function Chip(props: { swatch: Swatch }) {
  return (
    <span
      aria-hidden
      className="mt-px inline-block size-3 shrink-0 rounded-[3px] ring-1 ring-white/30"
      style={{ background: swatchBackground(props.swatch) }}
    />
  );
}

/** Imagem oficial da NOAA, sem alteração (fundo branco como na página do STAR). */
function OfficialImage(props: { image: LegendImage }) {
  const { image } = props;
  const url = legendImageUrl(image.file);
  const box = 'block w-full overflow-hidden rounded-md bg-white ring-1 ring-ink-600';
  if (image.crop) {
    // Mostra só a faixa pedida (linha de dia ou de noite); o arquivo é o original.
    const { y, height } = image.crop;
    const rest = image.height - height;
    const style: CSSProperties = {
      aspectRatio: `${image.width} / ${height}`,
      backgroundImage: `url("${url}")`,
      backgroundSize: `100% ${(image.height / height) * 100}%`,
      backgroundPosition: `0 ${rest > 0 ? (y / rest) * 100 : 0}%`,
      backgroundRepeat: 'no-repeat',
    };
    return <div role="img" aria-label={image.alt} className={box} style={style} />;
  }
  return (
    <img
      src={url}
      alt={image.alt}
      width={image.width}
      height={image.height}
      draggable={false}
      className={clsx(box, 'h-auto select-none')}
    />
  );
}

function Notes(props: { notes: LegendNote[] }) {
  const { notes } = props;
  return (
    <div
      className="grid gap-2 text-[11px] leading-tight text-ink-100"
      style={{ gridTemplateColumns: `repeat(${notes.length}, minmax(0, 1fr))` }}
    >
      {notes.map((n) => (
        <span key={n.text} className={clsx(n.align === 'center' && 'text-center', n.align === 'end' && 'text-right')}>
          {n.text}
        </span>
      ))}
    </div>
  );
}

function KeyItems(props: { group: KeyGroup; minRows: number }) {
  const { group, minRows } = props;
  // Linhas vazias (invisíveis) completam o grupo menor: a altura não muda entre dia e noite.
  const fill = Math.max(0, minRows * 2 - group.items.length);
  return (
    <ul className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] leading-tight text-ink-100">
      {Array.from({ length: fill }, (_, i) => (
        <li key={`vazio-${i}`} aria-hidden className="invisible order-last">
          &nbsp;
        </li>
      ))}
      {group.items.map((item) => (
        <li key={`${item.n ?? ''}${item.text}`} className="flex min-w-0 items-start gap-1.5">
          <Chip swatch={item.swatch} />
          {item.n !== undefined && (
            <span className="w-3.5 shrink-0 text-right font-semibold text-ink-300 tabular-nums">{item.n}</span>
          )}
          <span className="min-w-0">{item.text}</span>
        </li>
      ))}
    </ul>
  );
}

/** Escolha "Dia | Noite" das composições que mudam de receita. */
function DaypartSwitch(props: { value: Daypart; onChange: (d: Daypart) => void; label: string }) {
  const options: { value: Daypart; label: string }[] = [
    { value: 'day', label: 'Dia' },
    { value: 'night', label: 'Noite' },
  ];
  return (
    <div
      role="group"
      aria-label={props.label}
      className="inline-flex rounded-lg border border-ink-600 bg-ink-800 p-0.5"
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={props.value === o.value}
          onClick={() => props.onChange(o.value)}
          className={clsx(
            'rounded-md px-2.5 py-0.5 text-[11px] font-medium transition-colors pointer-coarse:py-1.5',
            'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400',
            props.value === o.value ? 'bg-ink-600 text-white' : 'text-ink-300 hover:text-ink-100',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** De onde vem a legenda: escala oficial da NOAA ou só o guia de interpretação. */
export function sourcePrefix(legend: ProductLegend): string {
  if (isOfficialLegend(legend)) return 'Escala oficial: ';
  if (legend.kind === 'gradient') return 'Sem escala oficial; interpretação: ';
  return 'Cores do guia (sem escala oficial): ';
}

function Sources(props: { legend: ProductLegend }) {
  const { legend } = props;
  return (
    <p className="text-[11px] leading-snug text-ink-300">
      {sourcePrefix(legend)}
      {legend.sources.map((s, i) => (
        <span key={s.url}>
          {i > 0 && ' · '}
          <a
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-ink-200 underline decoration-ink-500 underline-offset-2 hover:text-white"
          >
            {s.label}
          </a>
        </span>
      ))}
    </p>
  );
}

function KeyBody(props: { legend: KeyLegend; uid: string; autoDaypart: Daypart | null }) {
  const { legend, uid, autoDaypart } = props;
  const key = `${uid}:${legend.productId}`;
  const chosen = useLegendStore((s) => s.daypart[key]);
  const setDaypart = useLegendStore((s) => s.setDaypart);
  const withDayparts = hasDayparts(legend);
  const daypart = chosen ?? autoDaypart ?? 'day';
  const group = pickGroup(legend, withDayparts ? daypart : null);
  return (
    <>
      {withDayparts && (
        <div className="flex items-center gap-2">
          <DaypartSwitch
            value={daypart}
            onChange={(d) => setDaypart(key, d)}
            label="Mostrar as cores de dia ou de noite"
          />
          {!chosen && autoDaypart && (
            <span className="text-[11px] text-ink-300">
              {autoDaypart === 'day' ? 'dia no quadro atual' : 'noite no quadro atual'}
            </span>
          )}
        </div>
      )}
      {group.image && <OfficialImage image={group.image} />}
      <KeyItems group={group} minRows={withDayparts ? keyRows(legend) : 0} />
    </>
  );
}

function LegendBody(props: {
  legend: ProductLegend;
  uid: string;
  autoDaypart: Daypart | null;
  showTitle: boolean;
  showHint: boolean;
  showSources: boolean;
}) {
  const { legend, uid, autoDaypart, showTitle, showHint, showSources } = props;
  // A base sem imagem neste quadro (a NOAA parou, loop/semNoaa.ts): as cores abaixo não estão na tela.
  const semBase = useSemBaseNoQuadro();
  const ehBase = useGoesStore((s) => s.layers[0]?.uid === uid);
  return (
    <div className="mt-1 space-y-1.5">
      {semBase && ehBase && (
        <p className="text-[11px] leading-snug text-yellow-200" data-legenda-sem-base="">
          Sem esta imagem neste quadro (a NOAA parou). Ao fundo, a foto fixa do Sentinel-2 (mosaico de 2025) — não é de agora.
        </p>
      )}
      {/* No celular o título fica só para leitores de tela (a escala e os rótulos já explicam). */}
      <p className={showTitle ? 'text-[11px] leading-snug text-ink-200' : 'sr-only'}>{legend.title}</p>
      {/* Sem a imagem no quadro, as cores dela não estão na tela: fica só o aviso. */}
      {!(semBase && ehBase) && legend.kind === 'colorbar' && (
        <>
          <OfficialImage image={legend.image} />
          <Notes notes={legend.notes} />
        </>
      )}
      {!(semBase && ehBase) && legend.kind === 'gradient' && (
        <>
          <div
            aria-hidden
            className="h-3 rounded-sm ring-1 ring-ink-500"
            style={{ background: `linear-gradient(to right, ${legend.stops.join(', ')})` }}
          />
          <Notes notes={legend.notes} />
        </>
      )}
      {!(semBase && ehBase) && legend.kind === 'key' && <KeyBody legend={legend} uid={uid} autoDaypart={autoDaypart} />}
      {!(semBase && ehBase) && legend.hint && showHint && <p className="text-[11px] leading-snug text-ink-300">{legend.hint}</p>}
      {showSources && <Sources legend={legend} />}
    </div>
  );
}

/** "60%" quando a camada não está 100% opaca. */
function opacityLabel(opacity: number): string | null {
  return opacity < 0.995 ? `${Math.round(opacity * 100)}%` : null;
}

function OpacityTag(props: { opacity: number }) {
  const label = opacityLabel(props.opacity);
  if (!label) return null;
  return (
    <span className="shrink-0 text-[11px] text-ink-300" title="Opacidade da camada">
      {label}
    </span>
  );
}

/**
 * Bloco de uma camada. Com uma camada só, o nome dela vai no cabeçalho do
 * painel; com 2–3, cada bloco tem um cabeçalho que abre/fecha.
 */
function LayerBlock(props: {
  layer: LegendLayer;
  multi: boolean;
  /** Celular ou mapa baixo: só escala + rótulos. */
  compact: boolean;
  defaultOpen: boolean;
  autoDaypart: Daypart | null;
}) {
  const { layer, multi, compact, defaultOpen, autoDaypart } = props;
  const legend = getLegend(layer.productId);
  const stored = useLegendStore((s) => s.open[layer.uid]);
  const setOpen = useLegendStore((s) => s.setOpen);
  const bodyId = useId();
  const name = getProductName(layer.productId);

  if (layer.ausente) {
    return (
      <p className="text-[11px] leading-snug text-ink-300" data-legenda-ausente="">
        <span className="font-semibold text-ink-200">{name}</span> — sem imagem neste horário (a NOAA não publicou esta varredura).
      </p>
    );
  }
  if (layer.covered) {
    return (
      <p className="text-[11px] leading-snug text-ink-300">
        <span className="font-semibold text-ink-200">{name}</span> — escondida pela camada de cima (opacidade 100%).
      </p>
    );
  }
  if (!legend) return null;
  const body = (
    <LegendBody
      legend={legend}
      uid={layer.uid}
      autoDaypart={autoDaypart}
      // Sem a frase de ajuda com 2–3 camadas; no modo compacto, só escala + rótulos (fontes no "Sobre").
      showTitle={!compact}
      showHint={!multi && !compact}
      showSources={!compact}
    />
  );
  if (!multi) return body;
  const open = stored ?? defaultOpen;
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen(layer.uid, !open)}
        className="-mx-1 flex w-[calc(100%+0.5rem)] items-center gap-1.5 rounded-md px-1 py-0.5 text-left hover:bg-ink-700/60 focus-visible:outline-2 focus-visible:outline-mata-400 pointer-coarse:py-2"
      >
        <span className="truncate text-xs font-semibold text-ink-100">{name}</span>
        <OpacityTag opacity={layer.opacity} />
        <ChevronDown
          aria-hidden
          className={clsx('ml-auto size-3.5 shrink-0 text-ink-300 transition-transform', !open && '-rotate-90')}
        />
      </button>
      {open && <div id={bodyId}>{body}</div>}
    </div>
  );
}

/** Chave do bloco do radar no estado de aberto/fechado (não colide com os uids do GOES). */
const RADAR_UID = 'radar';

/** Bloco do radar (SIPAM): mesma forma dos blocos das camadas do satélite. */
function RadarBlock(props: { produto: Produto; opacity: number; multi: boolean; compact: boolean; defaultOpen: boolean }) {
  const { produto, opacity, multi, compact, defaultOpen } = props;
  const def = DEF_PRODUTO[produto];
  const stored = useLegendStore((s) => s.open[RADAR_UID]);
  const setOpen = useLegendStore((s) => s.setOpen);
  const bodyId = useId();
  const body = (
    <div className="mt-1 space-y-1.5">
      <p className={compact ? 'sr-only' : 'text-[11px] leading-snug text-ink-200'}>{def.titulo}</p>
      <LegendaRadar produto={produto} compacta />
      {!compact && <p className="text-[11px] leading-snug text-ink-300">Escala oficial: SIPAM/Censipam</p>}
    </div>
  );
  if (!multi) return body;
  const open = stored ?? defaultOpen;
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen(RADAR_UID, !open)}
        className="-mx-1 flex w-[calc(100%+0.5rem)] items-center gap-1.5 rounded-md px-1 py-0.5 text-left hover:bg-ink-700/60 focus-visible:outline-2 focus-visible:outline-mata-400 pointer-coarse:py-2"
      >
        <span className="truncate text-xs font-semibold text-ink-100">Radar · {def.nome}</span>
        <OpacityTag opacity={opacity} />
        <ChevronDown
          aria-hidden
          className={clsx('ml-auto size-3.5 shrink-0 text-ink-300 transition-transform', !open && '-rotate-90')}
        />
      </button>
      {open && <div id={bodyId}>{body}</div>}
    </div>
  );
}

/** Chave do bloco da chuva por satélite no estado de aberto/fechado. */
const GSMAP_UID = 'gsmap';

/** Bloco da chuva por satélite (GSMaP): a escala da chuva do "Próximos dias", a mesma do mapa. */
function GsmapBlock(props: { opacity: number; multi: boolean; compact: boolean; defaultOpen: boolean }) {
  const { opacity, multi, compact, defaultOpen } = props;
  const stored = useLegendStore((s) => s.open[GSMAP_UID]);
  const setOpen = useLegendStore((s) => s.setOpen);
  const bodyId = useId();
  const q = useHoraNaTela();
  const hora = q ? `${formatTimeBrt(q.ini)} a ${formatTimeBrt(q.fim)}` : null;
  const body = (
    <div className="mt-1 space-y-1.5">
      {hora ? (
        <>
          <p className="text-[11px] leading-snug text-ink-200 tabular-nums">
            {compact ? `Estimativa · média de ${hora}` : `Chuva média de ${hora} estimada por satélite (mm/h)`}
          </p>
          <Escala paleta={PALETA_GSMAP} compacta />
        </>
      ) : (
        <p className="text-[11px] leading-snug text-ink-200">Ainda sem estimativa de chuva para este horário.</p>
      )}
      {!compact && <p className="text-[11px] leading-snug text-ink-300">Estimativa, não medição: GSMaP (JAXA)</p>}
    </div>
  );
  if (!multi) return body;
  const open = stored ?? defaultOpen;
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen(GSMAP_UID, !open)}
        className="-mx-1 flex w-[calc(100%+0.5rem)] items-center gap-1.5 rounded-md px-1 py-0.5 text-left hover:bg-ink-700/60 focus-visible:outline-2 focus-visible:outline-mata-400 pointer-coarse:py-2"
      >
        <span className="truncate text-xs font-semibold text-ink-100">Chuva por satélite · mm/h</span>
        <OpacityTag opacity={opacity} />
        <ChevronDown
          aria-hidden
          className={clsx('ml-auto size-3.5 shrink-0 text-ink-300 transition-transform', !open && '-rotate-90')}
        />
      </button>
      {open && <div id={bodyId}>{body}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Posição: acima dos créditos quando eles chegam ao canto esquerdo
// ---------------------------------------------------------------------------

/** Mapa mais baixo que isto (px): legenda compacta, como no celular. */
const SHORT_MAP = 560;

/**
 * Distância do fundo do mapa (sobe acima dos créditos quando eles chegam ao
 * canto esquerdo) e se o mapa é baixo (legenda compacta).
 */
function usePanelPlacement(
  panelRef: RefObject<HTMLElement | null>,
  base: number,
  deps: unknown,
): { bottom: number; short: boolean } {
  const [bottom, setBottom] = useState(base);
  const [short, setShort] = useState(false);
  useLayoutEffect(() => {
    const panel = panelRef.current;
    const host = panel?.parentElement;
    if (!panel || !host) return;
    const attribution = host.querySelector<HTMLElement>('.amaview-attribution');
    const update = () => {
      let next = base;
      const h = host.getBoundingClientRect();
      if (attribution) {
        const a = attribution.getBoundingClientRect();
        const p = panel.getBoundingClientRect();
        const overlapsX = a.width > 0 && a.height > 0 && a.left < p.right + 8 && a.right > p.left - 8;
        if (overlapsX) next = Math.max(base, Math.round(h.bottom - a.top) + 8);
      }
      setBottom((b) => (b === next ? b : next));
      setShort(h.height > 0 && h.height < SHORT_MAP);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(host);
    ro.observe(panel);
    if (attribution) ro.observe(attribution);
    return () => ro.disconnect();
  }, [panelRef, base, deps]);
  return { bottom, short };
}

// ---------------------------------------------------------------------------
// Painel
// ---------------------------------------------------------------------------

/** Se a legenda falhar, o mapa segue sem ela (nada de mensagem no meio da imagem). */
class LegendBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[AmaView] Falha na legenda das cores:', error, info.componentStack);
  }

  override render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}

/** Legenda das cores (montada pelo app sobre o mapa, dentro do <main>). */
export function MapLegend(props: { mobile: boolean }) {
  // No celular a legenda não fica no mapa (pedido do usuário, 29/10/2026): cada
  // camada mostra a dela no próprio painel da folha (`LegendaDasImagens` no do
  // satélite; o radar e a chuva já tinham a escala no painel).
  if (props.mobile) return null;
  return (
    <LegendBoundary>
      <MapLegendPanel mobile={props.mobile} />
    </LegendBoundary>
  );
}

/**
 * A legenda das imagens do satélite fora do mapa: no painel "Satélite" da folha
 * do celular. Os mesmos blocos da legenda flutuante, com título e fontes (a
 * folha tem espaço).
 */
export function LegendaDasImagens() {
  const stack = useGoesStore((s) => s.layers);
  const frameStatus = usePlanStore((s) => s.frameStatus);
  const layers = useMemo(
    () => legendLayers(stack.map((l, i) => ({ ...l, ausente: i > 0 && (frameStatus[l.uid] === 'none' || frameStatus[l.uid] === 'failed') }))),
    [stack, frameStatus],
  );
  const autoDaypart = useTimeStore((s) => {
    const t = shownTimeOf(s);
    return t === null ? null : daypartAt(t);
  });
  if (layers.length === 0) return <p className="text-xs text-ink-400">Ligue uma imagem para ver a legenda das cores.</p>;
  const shown = layers.filter((l) => !l.covered && !l.ausente);
  const multi = shown.length > 1;
  return (
    <LegendBoundary>
      <div className="space-y-2 divide-y divide-ink-700/70 [&>*+*]:pt-2" data-testid="legenda-no-painel">
        {layers.map((layer) => (
          <LayerBlock key={layer.uid} layer={layer} multi={multi} compact={false} defaultOpen={layer === shown[0]} autoDaypart={autoDaypart} />
        ))}
      </div>
    </LegendBoundary>
  );
}

function MapLegendPanel(props: { mobile: boolean }) {
  const { mobile } = props;
  const stack = useGoesStore((s) => s.layers);
  // Camada de cima sem a mesma varredura neste quadro (loop/frames.ts, `ALIGN_TOLERANCE_MS`):
  // não está na tela, então nem cobre a base nem mostra a escala dela.
  const frameStatus = usePlanStore((s) => s.frameStatus);
  const layers = useMemo(
    () => legendLayers(stack.map((l, i) => ({ ...l, ausente: i > 0 && (frameStatus[l.uid] === 'none' || frameStatus[l.uid] === 'failed') }))),
    [stack, frameStatus],
  );
  // O radar fica acima do satélite no mapa: o bloco dele vem primeiro.
  const radarOn = useRadarStore((s) => s.visivel);
  const radarProduto = useRadarStore((s) => s.produto);
  const radarOpacity = useRadarStore((s) => s.opacidade);
  // A chuva por satélite fica entre o satélite e o radar: o bloco dela, entre os dois.
  const gsmapOn = useGsmapStore((s) => s.visivel);
  const gsmapOpacity = useGsmapStore((s) => s.opacidade);
  const extras = (radarOn ? 1 : 0) + (gsmapOn ? 1 : 0);
  const collapsed = useLegendStore((s) => s.collapsed);
  const setCollapsed = useLegendStore((s) => s.setCollapsed);
  // Só muda quando o quadro atual passa do dia para a noite (não a cada quadro).
  const autoDaypart = useTimeStore((s) => {
    const t = shownTimeOf(s);
    return t === null ? null : daypartAt(t);
  });
  const panelRef = useRef<HTMLElement>(null);
  const titleId = useId();
  // O painel lateral flutua sobre a borda esquerda do mapa (desktop): a legenda fica ao lado dele.
  const painel = usePainelLateralAberto() && !mobile ? SIDEBAR_WIDTH : 0;
  const edge = (mobile ? EDGE_MOBILE : EDGE_DESKTOP) + painel;
  const deitado = useDeitado() && mobile;
  // No celular a legenda recolhida fica na borda; aberta, sobe acima do play e da previsão.
  const base = mobile ? (collapsed ? EDGE_MOBILE : (deitado ? MOBILE_BOTTOM_DEITADO : MOBILE_BOTTOM) + 16) : EDGE_DESKTOP;
  const { bottom, short } = usePanelPlacement(panelRef, base, collapsed || (layers.length === 0 && extras === 0));

  if (layers.length === 0 && extras === 0) return null;
  const shown = layers.filter((l) => !l.covered && !l.ausente);
  const multi = shown.length + extras > 1;
  const single = multi || extras > 0 ? null : shown[0];
  const radarOnly = radarOn && !multi;
  const gsmapOnly = gsmapOn && !multi;

  if (collapsed) {
    return (
      <section
        ref={panelRef}
        aria-label="Legenda das cores"
        className="absolute z-10"
        // Celular: no canto inferior direito, na linha da coluna de botões (ver `MOBILE_BOTTOM`).
        // Deitado, o botão da previsão fica no canto (tracking/BotaoDaPrevisao), e a legenda, à esquerda dele.
        style={mobile ? { right: deitado ? EDGE_MOBILE_RIGHT + 44 + 8 : EDGE_MOBILE_RIGHT, bottom } : { left: edge, bottom }}
      >
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          aria-expanded={false}
          className="glass inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-ink-100 shadow-lg hover:bg-ink-800 focus-visible:outline-2 focus-visible:outline-mata-400 pointer-coarse:py-2.5"
        >
          <Palette className="size-3.5 text-mata-400" aria-hidden />
          Legenda
          <ChevronUp className="size-3.5 text-ink-300" aria-hidden />
        </button>
      </section>
    );
  }

  return (
    <section
      ref={panelRef}
      aria-labelledby={titleId}
      data-testid="map-legend"
      className="glass absolute z-10 flex flex-col overflow-hidden rounded-xl shadow-lg"
      style={
        mobile
          ? {
              // À direita do trilho de camadas (`--trilho-fim`, index.css) até a coluna da direita.
              left: 'var(--trilho-fim)',
              bottom,
              width: `min(calc(100% - var(--trilho-fim) - ${EDGE_MOBILE_RIGHT}px), 420px)`,
              // Deitado, a legenda (até 420 px) não chega à coluna da direita: só o chip do topo limita.
              maxHeight: `min(calc(100% - ${bottom + (deitado ? TOP_CLEARANCE : TOP_CLEARANCE_MOBILE)}px), 420px)`,
            }
          : {
              left: edge,
              bottom,
              width: `min(376px, calc(100% - ${88 + painel}px))`,
              maxHeight: `min(calc(100% - ${bottom + TOP_CLEARANCE}px), 620px)`,
            }
      }
    >
      <header className="flex items-center gap-1.5 py-1 pr-1 pl-2.5">
        <Palette className="size-3.5 shrink-0 text-mata-400" aria-hidden />
        <h2 id={titleId} className="min-w-0 truncate text-xs font-semibold text-ink-100">
          {single ? (
            <>
              <span className="sr-only">Legenda das cores: </span>
              {getProductName(single.productId)}
            </>
          ) : radarOnly ? (
            <>
              <span className="sr-only">Legenda das cores: </span>
              Radar · {DEF_PRODUTO[radarProduto].nome}
            </>
          ) : gsmapOnly ? (
            <>
              <span className="sr-only">Legenda das cores: </span>
              Chuva por satélite · mm/h
            </>
          ) : (
            'Legenda das cores'
          )}
        </h2>
        {single && <OpacityTag opacity={single.opacity} />}
        {radarOnly && <OpacityTag opacity={radarOpacity} />}
        {gsmapOnly && <OpacityTag opacity={gsmapOpacity} />}
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          aria-expanded
          aria-label="Recolher a legenda"
          title="Recolher a legenda"
          className="ml-auto inline-flex size-7 items-center justify-center rounded-md text-ink-300 hover:bg-ink-700/70 hover:text-white focus-visible:outline-2 focus-visible:outline-mata-400 pointer-coarse:size-10"
        >
          <ChevronDown className="size-4" aria-hidden />
        </button>
      </header>
      <div className="scroll-thin min-h-0 flex-1 space-y-2 divide-y divide-ink-700/70 overflow-y-auto overscroll-contain px-2.5 pb-2.5 [&>*+*]:pt-2">
        {radarOn && (
          <RadarBlock
            produto={radarProduto}
            opacity={radarOpacity}
            multi={multi}
            compact={mobile || short}
            defaultOpen
          />
        )}
        {gsmapOn && <GsmapBlock opacity={gsmapOpacity} multi={multi} compact={mobile || short} defaultOpen={!radarOn} />}
        {layers.map((layer) => (
          <LayerBlock
            key={layer.uid}
            layer={layer}
            multi={multi}
            compact={mobile || short}
            // Com 2–3 camadas só a de cima NA TELA começa aberta (as outras abrem com um toque).
            defaultOpen={layer === shown[0] && extras === 0}
            autoDaypart={autoDaypart}
          />
        ))}
      </div>
    </section>
  );
}
